import type { TFunction } from "i18next";

import type {
  InspectionAnswerOption,
  InspectionFieldDef,
  InspectionItemDef,
  InspectionOccurrenceDef,
  InspectionStepDef,
} from "./inspection-form.types";

const CATALOG_PREFIX = "inspection.catalog.v1.";

export function localizeCatalogText(
  value: string | undefined,
  translate: TFunction,
): string {
  if (!value) return "";
  if (value.startsWith(CATALOG_PREFIX)) return translate(value);
  return value;
}

const localizeOption = (
  option: InspectionAnswerOption,
  translate: TFunction,
): InspectionAnswerOption => ({
  ...option,
  label: localizeCatalogText(option.label, translate),
});

const localizeField = (
  field: InspectionFieldDef,
  translate: TFunction,
): InspectionFieldDef => ({
  ...field,
  label: field.label
    ? localizeCatalogText(field.label, translate)
    : field.label,
  placeholder: field.placeholder
    ? localizeCatalogText(field.placeholder, translate)
    : field.placeholder,
  options: field.options?.map((option) => localizeOption(option, translate)),
});

const localizeOccurrence = (
  occurrence: InspectionOccurrenceDef,
  translate: TFunction,
): InspectionOccurrenceDef => ({
  ...occurrence,
  addLabel: localizeCatalogText(occurrence.addLabel, translate),
  fields: occurrence.fields.map((field) => localizeField(field, translate)),
  grades: occurrence.grades?.map((grade) => localizeItem(grade, translate)),
});

const localizeItem = (
  item: InspectionItemDef,
  translate: TFunction,
): InspectionItemDef => ({
  ...item,
  label: localizeCatalogText(item.label, translate),
  sectionTitle: item.sectionTitle
    ? localizeCatalogText(item.sectionTitle, translate)
    : item.sectionTitle,
  groupLabel: item.groupLabel
    ? localizeCatalogText(item.groupLabel, translate)
    : item.groupLabel,
  observationsPlaceholder: item.observationsPlaceholder
    ? localizeCatalogText(item.observationsPlaceholder, translate)
    : item.observationsPlaceholder,
  options: item.options?.map((option) => localizeOption(option, translate)),
  fields: item.fields?.map((field) => localizeField(field, translate)),
  nested: item.nested?.map((nested) => localizeItem(nested, translate)),
  occurrences: item.occurrences
    ? localizeOccurrence(item.occurrences, translate)
    : item.occurrences,
});

/** Resolve `inspection.catalog.v1.*` no catálogo estático para o idioma da UI. */
export function localizeInspectionStep(
  step: InspectionStepDef,
  translate: TFunction,
): InspectionStepDef {
  return {
    ...step,
    title: localizeCatalogText(step.title, translate),
    hint: step.hint ? localizeCatalogText(step.hint, translate) : step.hint,
    gate: step.gate ? localizeItem(step.gate, translate) : step.gate,
    items: step.items.map((item) => localizeItem(item, translate)),
  };
}
